import { useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import {
  createInvite,
  deleteInvite,
  listInvites,
  type Invite,
  type InviteStatus,
} from "@/lib/api"

/**
 * Invites — "the guestbook."
 *
 * Aesthetic: letterpress correspondence. A composer card up top, a freshly
 * minted invitation revealed as a serif card with the code in display mono,
 * and the historical ledger below — pending first (the live ones), then
 * redeemed (with the recipient's name), then expired (most dimmed). No
 * shadows, hairline rules, all editorial.
 *
 * Concept choices:
 *  - The hint field is a *note to self* ("who is this for?"), not a recipient
 *    address — invites are claimed by whoever holds the code first. Framing
 *    it as a hint matches the back-end column name and avoids implying the
 *    server emails anyone (it doesn't, today).
 *  - TTL chips are 7 / 14 / 30 / 60. Defaults to 14.
 *  - The full magic-link URL is composed client-side from window.location;
 *    no server round-trip needed and self-hosting "just works."
 */

const TTL_CHOICES = [
  { days: 7, label: "1 week" },
  { days: 14, label: "2 weeks" },
  { days: 30, label: "1 month" },
  { days: 60, label: "2 months" },
] as const

export function InvitesPage() {
  const auth = useAuth()
  const [invites, setInvites] = useState<Invite[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // Freshly minted in this session — keeps the reveal card stable across
  // refresh/list-poll cycles. Set to the code string.
  const [justMinted, setJustMinted] = useState<string | null>(null)

  async function refresh() {
    try {
      const xs = await listInvites()
      setInvites(xs)
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Couldn't load invites.")
    }
  }

  useEffect(() => {
    void refresh()
    // Re-poll once per minute so "expires in 3h" feels live without WS.
    const t = setInterval(refresh, 60_000)
    return () => clearInterval(t)
  }, [])

  if (auth.status !== "authed") return null

  return (
    <Shell
      currentPath="/invites"
      pageEyebrow="invites"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3 animate-ink-in">invites · the guestbook</p>
        <h1
          className="font-display text-display tracking-tightest mb-3 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          Bring someone <span className="italic text-moss">in.</span>
        </h1>
        <p
          className="font-sans text-base text-muted-foreground max-w-prose leading-relaxed mb-12 animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          FitTrack is invite-only. Mint a one-time code, send it to a friend with
          the sign-in link, and revoke it anytime before they claim it.
        </p>

        <Composer
          onCreated={(inv) => {
            setJustMinted(inv.code)
            // Optimistic: prepend before the poll round-trip returns.
            setInvites((prev) => (prev ? [inv, ...prev] : [inv]))
            void refresh()
          }}
        />

        {err && (
          <p className="font-sans text-sm text-persimmon mt-6 animate-ink-in">
            {err}
          </p>
        )}

        {invites === null ? (
          <p className="eyebrow mt-16">loading…</p>
        ) : (
          <Ledger
            invites={invites}
            justMintedCode={justMinted}
            onChanged={refresh}
          />
        )}
      </div>
    </Shell>
  )
}

// ───────────────────────────────────────────────────────────── Composer ────

function Composer({ onCreated }: { onCreated: (inv: Invite) => void }) {
  const [hint, setHint] = useState("")
  const [ttlDays, setTtlDays] = useState<number>(14)
  // sendEmail tracks the toggle explicitly so we can preserve the user's
  // choice across edits without re-deriving from `hint` on every render.
  // Default behaviour: auto-send is on the moment a hint becomes a
  // plausible email; user can flip it off (then the toggle stays off).
  const [sendEmail, setSendEmail] = useState(true)
  const hintLooksEmail = /.+@.+\..+/.test(hint.trim())
  const willSend = sendEmail && hintLooksEmail
  const [state, setState] = useState<
    { kind: "idle" } | { kind: "minting" } | { kind: "error"; msg: string }
  >({ kind: "idle" })

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setState({ kind: "minting" })
    try {
      const inv = await createInvite({
        email_hint: hint,
        ttl_days: ttlDays,
        send_email: willSend,
      })
      setHint("")
      setTtlDays(14)
      setSendEmail(true)
      setState({ kind: "idle" })
      onCreated(inv)
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Couldn't mint invite."
      setState({ kind: "error", msg })
    }
  }

  return (
    <section
      aria-labelledby="composer-h"
      className="card-surface px-6 sm:px-10 py-8 sm:py-10 animate-fade-up"
      style={{ animationDelay: "360ms" }}
    >
      <div className="flex items-baseline justify-between gap-4 mb-6">
        <h2
          id="composer-h"
          className="font-display text-2xl sm:text-3xl tracking-tight"
        >
          Issue an <span className="italic text-moss">invitation</span>.
        </h2>
        <span className="eyebrow hidden sm:inline">draft</span>
      </div>

      <form onSubmit={submit} className="space-y-7">
        <div>
          <label htmlFor="hint" className="block eyebrow mb-2">
            Send to
          </label>
          <input
            id="hint"
            type="email"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="friend@somewhere.com"
            autoComplete="off"
            className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-sans text-lg tracking-tight transition-colors"
          />
          <p className="font-sans text-xs text-muted-foreground mt-2">
            We'll email the invitation directly. Leave blank if you'd rather
            mint a code and deliver it yourself.
          </p>
        </div>

        {/* Send toggle — only meaningful when there's a recipient. Stays
            visible (but disabled) when hint is empty so the option doesn't
            visually jump in/out as you type. */}
        <div>
          <button
            type="button"
            onClick={() => setSendEmail((s) => !s)}
            disabled={!hintLooksEmail}
            aria-pressed={willSend}
            className={`w-full flex items-center justify-between gap-4 py-3 border-b border-border text-left transition-colors ${
              hintLooksEmail
                ? "hover:border-foreground"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <span>
              <span className="block eyebrow mb-1">delivery</span>
              <span className="font-sans text-sm text-foreground">
                {willSend
                  ? "Email this to the recipient now"
                  : hintLooksEmail
                  ? "Skip auto-email — I'll deliver it myself"
                  : "Add an email above to enable auto-send"}
              </span>
            </span>
            <span
              aria-hidden
              className={`relative inline-block w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                willSend ? "bg-moss" : "bg-border"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-surface transition-transform ${
                  willSend ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </span>
          </button>
        </div>

        <div>
          <label className="block eyebrow mb-3">Good for</label>
          <div className="flex flex-wrap gap-2">
            {TTL_CHOICES.map((c, i) => {
              const active = c.days === ttlDays
              return (
                <button
                  key={c.days}
                  type="button"
                  onClick={() => setTtlDays(c.days)}
                  aria-pressed={active}
                  className={`px-4 py-2 border rounded transition-colors font-mono text-xs uppercase tracking-wider ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                  }`}
                >
                  <span aria-hidden className="font-mono text-[10px] opacity-50 mr-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {c.label}
                </button>
              )
            })}
          </div>
        </div>

        {state.kind === "error" && (
          <p className="font-sans text-sm text-persimmon">{state.msg}</p>
        )}

        <div className="flex items-center justify-between pt-2">
          <p className="eyebrow hidden sm:block">
            single-use · revocable · {ttlDays}-day expiry
          </p>
          <button
            type="submit"
            disabled={state.kind === "minting"}
            className="group bg-foreground text-background px-6 py-3 rounded font-sans text-sm tracking-tight transition-all hover:bg-moss disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="inline-flex items-center gap-3">
              {state.kind === "minting"
                ? willSend
                  ? "Sending…"
                  : "Minting…"
                : willSend
                ? "Send invitation"
                : "Mint invitation"}
              <span aria-hidden className="transition-transform group-hover:translate-x-1">
                →
              </span>
            </span>
          </button>
        </div>
      </form>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────── Ledger ────

function Ledger({
  invites,
  justMintedCode,
  onChanged,
}: {
  invites: Invite[]
  justMintedCode: string | null
  onChanged: () => Promise<void> | void
}) {
  const grouped = useMemo(() => groupByStatus(invites), [invites])

  if (invites.length === 0) {
    return (
      <div className="mt-16 text-center py-16 animate-fade-up" style={{ animationDelay: "480ms" }}>
        <p className="eyebrow mb-4">the guestbook</p>
        <p className="font-display italic text-3xl text-muted-foreground">
          empty — for now.
        </p>
      </div>
    )
  }

  const justMinted = invites.find((i) => i.code === justMintedCode)

  return (
    <div className="mt-16 space-y-14">
      {justMinted && (
        <FreshlyMinted invite={justMinted} onRevoked={onChanged} />
      )}

      <Section
        eyebrow="01 — pending"
        count={grouped.pending.length}
        invites={grouped.pending}
        renderRow={(inv) => (
          <PendingRow key={inv.code} invite={inv} onRevoked={onChanged} />
        )}
      />
      <Section
        eyebrow="02 — redeemed"
        count={grouped.redeemed.length}
        invites={grouped.redeemed}
        renderRow={(inv) => <RedeemedRow key={inv.code} invite={inv} />}
        muted
      />
      <Section
        eyebrow="03 — expired"
        count={grouped.expired.length}
        invites={grouped.expired}
        renderRow={(inv) => (
          <ExpiredRow key={inv.code} invite={inv} onDeleted={onChanged} />
        )}
        muted
      />
    </div>
  )
}

function groupByStatus(xs: Invite[]) {
  const pending: Invite[] = []
  const redeemed: Invite[] = []
  const expired: Invite[] = []
  for (const i of xs) {
    if (i.status === "pending") pending.push(i)
    else if (i.status === "redeemed") redeemed.push(i)
    else expired.push(i)
  }
  return { pending, redeemed, expired }
}

function Section({
  eyebrow,
  count,
  invites,
  renderRow,
  muted,
}: {
  eyebrow: string
  count: number
  invites: Invite[]
  renderRow: (inv: Invite) => React.ReactNode
  muted?: boolean
}) {
  if (count === 0) return null
  return (
    <section className={muted ? "opacity-90" : undefined}>
      <header className="flex items-baseline justify-between mb-5">
        <p className="eyebrow">{eyebrow}</p>
        <p className="font-mono text-xs text-muted-foreground tabular-nums">
          {String(count).padStart(2, "0")}
        </p>
      </header>
      <div className="rule" />
      <ul className="divide-y divide-border">
        {invites.map((inv) => (
          <li key={inv.code}>{renderRow(inv)}</li>
        ))}
      </ul>
    </section>
  )
}

// ─────────────────────────────────────────────────────── Row variants ────

function FreshlyMinted({
  invite,
  onRevoked,
}: {
  invite: Invite
  onRevoked: () => Promise<void> | void
}) {
  const inviter = useInviterName()
  const sent = Boolean(invite.email_sent)
  return (
    <section
      aria-label="freshly minted invitation"
      className="relative card-surface px-6 sm:px-10 py-8 sm:py-10 animate-fade-up overflow-hidden"
    >
      {/* faint dot-grid wash — letterpress paper texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />
      <div className="relative">
        <div className="flex items-baseline justify-between mb-6">
          <p className="eyebrow text-moss">
            {sent ? "✓ sent" : "just minted"}
          </p>
          <p className="eyebrow">{shortExpiry(invite.expires_at)}</p>
        </div>

        {sent && invite.email_hint && (
          <p className="font-sans text-sm text-foreground mb-6 -mt-2">
            Invitation emailed to{" "}
            <span className="font-display italic">{invite.email_hint}</span>.
            They'll get a one-tap claim link from{" "}
            <span className="font-mono text-xs">noreply@gofittrack.com</span>.
          </p>
        )}
        {invite.email_error && (
          <p className="font-sans text-sm text-persimmon mb-6 -mt-2">
            {invite.email_error} — copy the message and send it yourself.
          </p>
        )}

        <CodeDisplay code={invite.code} />

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <CopyChip
            label="Copy code"
            payload={invite.code}
            successLabel="Copied"
          />
          <CopyChip
            label="Copy invitation message"
            payload={composeInvitationMessage(invite, inviter)}
            successLabel="Message copied"
            primary
          />
          <span className="flex-1" />
          <RevokeButton code={invite.code} onDone={onRevoked} />
        </div>

        {invite.email_hint && (
          <p className="mt-6 font-sans text-sm text-muted-foreground">
            <span className="eyebrow mr-3">for</span>
            <span className="font-display italic text-foreground">
              {invite.email_hint}
            </span>
          </p>
        )}

        <MessagePreview message={composeInvitationMessage(invite, inviter)} />
      </div>
    </section>
  )
}

/**
 * MessagePreview — show the prose that "Copy invitation message" puts on
 * the clipboard, so the sender knows what their friend will read before
 * pasting. Collapsed by default to keep the freshly-minted card calm; one
 * tap opens it.
 */
function MessagePreview({ message }: { message: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-8 border-t border-border pt-6">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        className="eyebrow hover:text-foreground transition-colors inline-flex items-center gap-2"
      >
        <span>{open ? "hide message" : "preview message"}</span>
        <span
          aria-hidden
          className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}
        >
          ›
        </span>
      </button>
      {open && (
        <pre
          className="mt-4 font-sans text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap bg-muted/40 border border-border rounded p-5 animate-ink-in"
          style={{ fontFamily: 'inherit' }}
        >
          {message}
        </pre>
      )}
    </div>
  )
}

function PendingRow({
  invite,
  onRevoked,
}: {
  invite: Invite
  onRevoked: () => Promise<void> | void
}) {
  const inviter = useInviterName()
  return (
    <div className="py-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 sm:items-center">
      <div className="min-w-0">
        <div className="flex items-center gap-3 mb-2">
          <FormattedCode code={invite.code} />
          <StatusBadge status="pending" />
        </div>
        <p className="font-sans text-sm text-muted-foreground">
          {invite.email_hint ? (
            <>
              for{" "}
              <span className="font-display italic text-foreground">
                {invite.email_hint}
              </span>
              {" · "}
            </>
          ) : null}
          minted {relativeDate(invite.created_at)} · {shortExpiry(invite.expires_at)}
        </p>
      </div>
      <div className="flex items-center gap-2 justify-start sm:justify-end">
        <CopyChip
          label="Copy code"
          payload={invite.code}
          successLabel="Copied"
        />
        <CopyChip
          label="Copy message"
          payload={composeInvitationMessage(invite, inviter)}
          successLabel="Copied"
        />
        <RevokeButton code={invite.code} onDone={onRevoked} />
      </div>
    </div>
  )
}

/**
 * useInviterName — pick the best display name for the signature line of the
 * invitation message. Display-name if set, otherwise the email's local-part
 * (Title-Cased), otherwise the full email. Returns "" if unauthed (shouldn't
 * happen — page is gated).
 */
function useInviterName(): string {
  const auth = useAuth()
  if (auth.status !== "authed") return ""
  const u = auth.me.user
  const dn = (u.display_name || "").trim()
  if (dn) return dn
  const local = u.email.split("@")[0] || u.email
  return local.charAt(0).toUpperCase() + local.slice(1)
}

function RedeemedRow({ invite }: { invite: Invite }) {
  const name = invite.used_by_name || invite.used_by_email || "someone"
  return (
    <div className="py-6">
      <div className="flex items-center gap-3 mb-2">
        <FormattedCode code={invite.code} muted />
        <StatusBadge status="redeemed" />
      </div>
      <p className="font-sans text-sm text-muted-foreground">
        claimed by{" "}
        <span className="font-display italic text-foreground">{name}</span>
        {invite.used_at ? <> · {relativeDate(invite.used_at)}</> : null}
      </p>
    </div>
  )
}

function ExpiredRow({
  invite,
  onDeleted,
}: {
  invite: Invite
  onDeleted: () => Promise<void> | void
}) {
  return (
    <div className="py-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:items-center">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <FormattedCode code={invite.code} muted strike />
          <StatusBadge status="expired" />
        </div>
        <p className="font-sans text-sm text-muted-foreground">
          {invite.email_hint ? (
            <>
              for{" "}
              <span className="italic">{invite.email_hint}</span>
              {" · "}
            </>
          ) : null}
          expired {relativeDate(invite.expires_at)}
        </p>
      </div>
      <RevokeButton
        code={invite.code}
        onDone={onDeleted}
        label="Forget"
        confirmText="Remove this expired invite from the guestbook?"
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────── Sub-components ────

function CodeDisplay({ code }: { code: string }) {
  const parts = formatCode(code).split("·")
  return (
    <div className="flex items-baseline gap-3 sm:gap-4 flex-wrap">
      {parts.map((part, i) => (
        <span key={i} className="contents">
          {i > 0 && (
            <span aria-hidden className="text-muted-foreground/40 font-mono text-3xl sm:text-5xl">
              ·
            </span>
          )}
          <span
            className="font-mono uppercase tabular-nums tracking-[0.18em] text-3xl sm:text-5xl text-foreground"
            style={{ fontVariationSettings: '"wght" 500' }}
          >
            {part}
          </span>
        </span>
      ))}
    </div>
  )
}

function FormattedCode({
  code,
  muted,
  strike,
}: {
  code: string
  muted?: boolean
  strike?: boolean
}) {
  return (
    <span
      className={`font-mono text-sm sm:text-base tracking-wider tabular-nums whitespace-nowrap ${
        muted ? "text-muted-foreground" : "text-foreground"
      } ${strike ? "line-through decoration-1" : ""}`}
    >
      {formatCode(code)}
    </span>
  )
}

function StatusBadge({ status }: { status: InviteStatus }) {
  const map: Record<InviteStatus, { bg: string; fg: string; label: string }> = {
    pending: { bg: "bg-moss/10", fg: "text-moss", label: "pending" },
    redeemed: {
      bg: "bg-foreground/[0.06]",
      fg: "text-foreground/70",
      label: "redeemed",
    },
    expired: {
      bg: "bg-persimmon/10",
      fg: "text-persimmon",
      label: "expired",
    },
  }
  const m = map[status]
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-[3px] rounded-sm ${m.bg} ${m.fg}`}
    >
      {m.label}
    </span>
  )
}

function CopyChip({
  label,
  payload,
  successLabel,
  primary,
}: {
  label: string
  payload: string
  successLabel: string
  primary?: boolean
}) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)
  async function copy() {
    try {
      await navigator.clipboard.writeText(payload)
      setCopied(true)
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), 1800)
    } catch {
      // Fallback: select-and-prompt is overkill; just silently ignore on
      // non-secure contexts. Users on http+IP will see the code visibly
      // and can copy manually.
    }
  }
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current)
  }, [])

  const base = "font-mono text-[11px] uppercase tracking-wider px-3 py-2 rounded border transition-all"
  const cls = primary
    ? `${base} border-foreground bg-foreground text-background hover:bg-moss hover:border-moss`
    : `${base} border-border text-muted-foreground hover:border-foreground hover:text-foreground`
  return (
    <button type="button" onClick={copy} className={cls} aria-live="polite">
      {copied ? `✓ ${successLabel}` : label}
    </button>
  )
}

function RevokeButton({
  code,
  onDone,
  label = "Revoke",
  confirmText = "Revoke this invite? The code stops working immediately.",
}: {
  code: string
  onDone: () => Promise<void> | void
  label?: string
  confirmText?: string
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function go() {
    if (!window.confirm(confirmText)) return
    setBusy(true)
    setErr(null)
    try {
      await deleteInvite(code)
      await onDone()
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Couldn't revoke.")
    } finally {
      setBusy(false)
    }
  }
  return (
    <span className="inline-flex items-baseline gap-3">
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 rounded border border-border text-muted-foreground hover:border-persimmon hover:text-persimmon transition-colors disabled:opacity-40"
      >
        {busy ? "…" : label}
      </button>
      {err && <span className="font-sans text-xs text-persimmon">{err}</span>}
    </span>
  )
}

// ──────────────────────────────────────────────────────────── Helpers ────

/** Pretty-print "ABCD-EFGH" with mid-dot separators, mirroring the Login placeholder. */
function formatCode(code: string): string {
  const clean = code.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
  if (clean.length <= 4) return clean
  const mid = Math.ceil(clean.length / 2)
  return `${clean.slice(0, mid)}·${clean.slice(mid)}`
}

/** "in 13 days" / "in 4 hours" / "3 hours ago" / "yesterday" — concise. */
function relativeDate(iso: string): string {
  const t = new Date(iso).getTime()
  const now = Date.now()
  const diffMs = t - now
  const past = diffMs < 0
  const sec = Math.abs(diffMs) / 1000
  const min = sec / 60
  const hr = min / 60
  const day = hr / 24
  let v: number
  let unit: string
  if (sec < 60) {
    return past ? "just now" : "in a moment"
  } else if (min < 60) {
    v = Math.round(min); unit = v === 1 ? "minute" : "minutes"
  } else if (hr < 24) {
    v = Math.round(hr); unit = v === 1 ? "hour" : "hours"
  } else if (day < 30) {
    v = Math.round(day); unit = v === 1 ? "day" : "days"
  } else {
    // Fall back to absolute for longer spans — feels more honest.
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }
  return past ? `${v} ${unit} ago` : `in ${v} ${unit}`
}

/** "expires in 13 days" — used in the eyebrow / meta lines. */
function shortExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return "expired"
  return `expires ${relativeDate(iso)}`
}

/**
 * Compose the human-shareable invitation. The voice is first-person from
 * the inviter, leaning warm-but-restrained: you were chosen on purpose,
 * the app is in beta, and your feedback is the actual reason you're here.
 *
 * Picks up window.location so the self-hosted operator doesn't have to
 * substitute their own URL. The signature uses the inviter's display name
 * (or email local-part) so it doesn't read as automated.
 */
function composeInvitationMessage(inv: Invite, inviter: string): string {
  const base = `${window.location.protocol}//${window.location.host}`
  // Build a one-click URL when we know the recipient's email — the Login
  // page reads ?email + ?invite from the query string and pre-fills both
  // fields, so the invitee just taps "Send sign-in link" and waits for
  // the magic link to land. Falls back to the bare /login URL + visible
  // code when no email hint was set on the invite.
  const oneClick =
    inv.email_hint
      ? `${base}/login?` +
        new URLSearchParams({
          email: inv.email_hint,
          invite: inv.code,
        }).toString()
      : null
  const expires = new Date(inv.expires_at).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
  const sig = inviter ? `\n— ${inviter}` : ""

  const lines = [
    "Hey,",
    "",
    "You're one of a small handful of people I'm bringing into the FitTrack beta.",
    "",
    "It's a quiet, private meal & weight tracker I've been building — no streaks, no shame, no ads, no data harvesting. Log what you ate, see where you stand, move on with your day.",
    "",
    "Because it's still early, your feedback genuinely shapes what gets built next. If something feels off, broken, confusing, or just missing — tell me. That's the whole point of this round, and the reason you're on the short list.",
    "",
  ]
  if (oneClick) {
    lines.push(
      `Claim your spot — your email and invite code are baked in, just tap Send:`,
      "",
      `    ${oneClick}`,
      "",
      `(If the link looks awkward, you can also visit ${base}/login and enter code ${formatCode(inv.code)} manually.)`,
    )
  } else {
    lines.push(
      `Sign in at ${base}/login with this invite code:`,
      "",
      `    ${formatCode(inv.code)}`,
    )
  }
  lines.push("", `Single-use. Good through ${expires}.${sig}`)
  return lines.join("\n")
}
